/**
 * SuperpositionPage — Superposition RS/TN ou Progressions/TN
 *
 * Utilisé pour les deux modes via la même page.
 * Le mode est déterminé par les données retournées par l'API.
 *
 * 4 sections :
 * 1. Tableau de superposition des positions
 * 2. AS et MC annuels/progressés → maison natale
 * 3. Aspects inter-thèmes filtrables
 * 4. Points notables (synthèse automatique)
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors, ASTRO_SYMBOLS } from '../styles/tokens.js';
import type { SuperpositionData, AspectInterTheme, PositionSuperposee } from '../types/clients.js';

/** Symbole astrologique d'un signe */
function signeSymbol(signe: string): string {
  const key = signe.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return ASTRO_SYMBOLS[key] ?? '';
}

/** Symbole astrologique d'une planète */
function planeteSymbol(nom: string): string {
  const key = nom.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return ASTRO_SYMBOLS[key] ?? '';
}

/** Couleur du highlight */
function highlightStyle(type: 'angle' | 'conjonction' | 'retour' | null): React.CSSProperties {
  switch (type) {
    case 'angle': return { background: '#fef2f2', borderLeft: '3px solid #ef4444' };
    case 'conjonction': return { background: '#fefce8', borderLeft: '3px solid #eab308' };
    case 'retour': return { background: '#f0fdf4', borderLeft: '3px solid #22c55e' };
    default: return { borderLeft: '3px solid transparent' };
  }
}

/** Couleur badge nature aspect */
function natureBadgeStyle(nature: string): React.CSSProperties {
  switch (nature) {
    case 'harmonie': return { background: '#dcfce7', color: '#166534' };
    case 'tension': return { background: '#fef2f2', color: '#991b1b' };
    case 'fusion': return { background: '#eff6ff', color: '#1e40af' };
    default: return { background: '#f5f5f5', color: '#666' };
  }
}

type AspectFilter = 'tous' | 'conjonction' | 'tension' | 'harmonie' | 'angulaires' | 'personnelles';

/** Planètes personnelles (filtre progressions) */
const PERSONNELLES = new Set(['Soleil', 'Lune', 'Mercure', 'Vénus', 'Mars']);

export function SuperpositionRSPage() {
  const { id, chartId } = useParams<{ id: string; chartId: string }>();
  const [data, setData] = useState<SuperpositionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aspectFilter, setAspectFilter] = useState<AspectFilter>('tous');

  useEffect(() => {
    if (!id || !chartId) return;
    setLoading(true);
    setError('');

    apiFetch<SuperpositionData>(`/clients/${id}/charts/${chartId}/superposition`)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error ?? 'Erreur lors du chargement');
        }
      })
      .catch(() => setError('Erreur de connexion au serveur'))
      .finally(() => setLoading(false));
  }, [id, chartId]);

  const isProgressions = data?.superposition.mode === 'progressions';
  const suffix = isProgressions ? 'prog' : 'RS';

  // Filtrer les aspects
  const filteredAspects = useMemo(() => {
    if (!data) return [];
    const aspects = data.superposition.aspectsInterThemes;
    switch (aspectFilter) {
      case 'conjonction': return aspects.filter((a) => a.nature === 'fusion');
      case 'tension': return aspects.filter((a) => a.nature === 'tension');
      case 'harmonie': return aspects.filter((a) => a.nature === 'harmonie');
      case 'angulaires': return aspects.filter((a) => a.angulaire);
      case 'personnelles': return aspects.filter((a) => PERSONNELLES.has(a.planeteRS));
      default: return aspects;
    }
  }, [data, aspectFilter]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
        Calcul de la superposition...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{
        background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
        padding: '10px 14px', fontSize: 14, color: '#991b1b',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <i className="bi bi-exclamation-circle" />
        {error || 'Données introuvables'}
      </div>
    );
  }

  const { positions, axesAnnuels, pointsNotables, phaseLunaison } = data.superposition;
  const anneeLabel = isProgressions
    ? `Progressions ${data.rs.anneeRs ?? ''}`
    : data.rs.anneeRs ? `RS ${data.rs.anneeRs}` : 'Révolution Solaire';

  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 8, padding: '1.5rem',
    marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const sectionTitle: React.CSSProperties = {
    margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600, color: colors.text,
    display: 'flex', alignItems: 'center', gap: 8,
  };

  // Filtres aspects — ajouter "Personnelles" en mode progressions
  const filterOptions: [AspectFilter, string][] = [
    ['tous', 'Tous'],
    ['conjonction', 'Conjonctions'],
    ['tension', 'Tensions'],
    ['harmonie', 'Harmonies'],
    ['angulaires', 'Angulaires'],
  ];
  if (isProgressions) {
    filterOptions.push(['personnelles', 'Personnelles']);
  }

  return (
    <div>
      <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: colors.text }}>
        <i className="bi bi-layers" style={{ color: colors.accent, marginRight: 10 }} />
        Superposition {anneeLabel} / Thème Natal
      </h1>

      {/* Note orbe en mode progressions */}
      {isProgressions && (
        <p style={{ margin: '0 0 1rem', fontSize: 12, color: colors.textMid, fontStyle: 'italic' }}>
          Orbe maximum 1° pour tous les aspects progressés
        </p>
      )}

      {/* Cycle de lunaison progressée */}
      {isProgressions && phaseLunaison && (
        <div style={{
          ...card,
          border: '2px solid #7c3aed',
          background: 'rgba(124, 58, 237, 0.03)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: '#ede9fe', color: '#7c3aed',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20,
          }}>
            ☽
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>
              Cycle de lunaison progressée
            </div>
            <div style={{ fontSize: 15, color: '#7c3aed', fontWeight: 600, marginTop: 2 }}>
              {phaseLunaison}
            </div>
          </div>
        </div>
      )}

      {/* Légende */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', fontSize: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: '#ef4444', borderRadius: 2, display: 'inline-block' }} />
          Sur angle natal
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: '#eab308', borderRadius: 2, display: 'inline-block' }} />
          Conjonction {suffix}/TN
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 12, height: 12, background: '#22c55e', borderRadius: 2, display: 'inline-block' }} />
          Retour planétaire
        </span>
        {isProgressions && (
          <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 12, height: 12, background: '#7c3aed', borderRadius: 2, display: 'inline-block' }} />
              Changement de signe
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 12, height: 12, background: '#3b82f6', borderRadius: 2, display: 'inline-block' }} />
              Changement de maison
            </span>
          </>
        )}
      </div>

      {/* ── Section 1 : Tableau de superposition ── */}
      <div style={card}>
        <h2 style={sectionTitle}>
          <i className="bi bi-table" style={{ color: colors.accent }} />
          Positions superposées
        </h2>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: colors.textMid, fontWeight: 600 }}>Planète</th>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: colors.textMid, fontWeight: 600 }}>
                  Position {isProgressions ? 'Prog' : 'RS'}
                </th>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: colors.textMid, fontWeight: 600 }}>Position TN</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', color: colors.textMid, fontWeight: 600 }}>M. natale</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', color: colors.textMid, fontWeight: 600 }}>Écart</th>
                {isProgressions && (
                  <th style={{ textAlign: 'center', padding: '8px 10px', color: colors.textMid, fontWeight: 600 }}>Évolution</th>
                )}
              </tr>
            </thead>
            <tbody>
              {positions.map((pos) => (
                <PositionRow key={pos.nom} pos={pos} isProgressions={isProgressions} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Section 2 : AS et MC ── */}
      <div style={card}>
        <h2 style={sectionTitle}>
          <i className="bi bi-compass" style={{ color: colors.accent }} />
          {isProgressions ? 'Axes progressés' : 'Axes annuels'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* AS */}
          <AxeCard
            title={`Ascendant ${isProgressions ? 'progressé' : 'RS'}`}
            axe={axesAnnuels.ascendant}
            suffix={suffix}
            // Trouver le TN AS pour comparaison en mode prog
            tnSigne={isProgressions ? positions.find((p) => p.nom === 'Ascendant')?.tn?.signe : undefined}
          />
          {/* MC */}
          <AxeCard
            title={`MC ${isProgressions ? 'progressé' : 'RS'}`}
            axe={axesAnnuels.mc}
            suffix={suffix}
            tnSigne={isProgressions ? positions.find((p) => p.nom === 'MC')?.tn?.signe : undefined}
          />
        </div>
      </div>

      {/* ── Section 3 : Aspects inter-thèmes ── */}
      <div style={card}>
        <h2 style={sectionTitle}>
          <i className="bi bi-diagram-3" style={{ color: colors.accent }} />
          Aspects inter-thèmes ({isProgressions ? 'Prog' : 'RS'} × TN)
          <span style={{ fontSize: 12, fontWeight: 400, color: colors.textMuted, marginLeft: 8 }}>
            {filteredAspects.length} aspect{filteredAspects.length > 1 ? 's' : ''}
          </span>
        </h2>

        {/* Filtres */}
        <div style={{ display: 'flex', gap: 6, marginBottom: '1rem', flexWrap: 'wrap' }}>
          {filterOptions.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setAspectFilter(key)}
              style={{
                padding: '4px 12px', borderRadius: 20, border: `1px solid ${colors.border}`,
                background: aspectFilter === key ? colors.accent : '#fff',
                color: aspectFilter === key ? '#fff' : colors.textMid,
                fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Liste des aspects */}
        <div style={{ display: 'grid', gap: 4 }}>
          {filteredAspects.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
              Aucun aspect pour ce filtre
            </div>
          ) : (
            filteredAspects.map((asp, i) => (
              <AspectRow key={i} aspect={asp} suffix={suffix} />
            ))
          )}
        </div>
      </div>

      {/* ── Section 4 : Points notables ── */}
      {pointsNotables.length > 0 && (
        <div style={{
          ...card,
          border: `2px solid ${colors.accent}`,
          background: `${colors.accent}08`,
        }}>
          <h2 style={sectionTitle}>
            <i className="bi bi-star" style={{ color: colors.accent }} />
            Points notables
          </h2>

          <div style={{ display: 'grid', gap: 8 }}>
            {pointsNotables.map((pt, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 6,
                background: '#fff',
                fontSize: 13,
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  ...categorieIconStyle(pt.categorie),
                }}>
                  {categorieIcon(pt.categorie)}
                </span>
                <span>{pt.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Ligne du tableau de positions */
function PositionRow({ pos, isProgressions }: { pos: PositionSuperposee; isProgressions: boolean }) {
  return (
    <tr style={{ ...highlightStyle(pos.highlight), borderBottom: `1px solid ${colors.bgPage}` }}>
      <td style={{ padding: '8px 10px', fontWeight: 600 }}>
        <span style={{ marginRight: 4 }}>{planeteSymbol(pos.nom)}</span>
        {pos.nom}
        {pos.rs.retrograde && <span style={{ color: colors.danger, marginLeft: 4, fontSize: 11 }}>℞</span>}
      </td>
      <td style={{ padding: '8px 10px' }}>
        {signeSymbol(pos.rs.signe)} {pos.rs.degre} {pos.rs.signe}
        {pos.rs.maison > 0 && <span style={{ color: colors.textMuted, marginLeft: 6 }}>M.{pos.rs.maison}</span>}
      </td>
      <td style={{ padding: '8px 10px' }}>
        {pos.tn ? (
          <>
            {signeSymbol(pos.tn.signe)} {pos.tn.degre} {pos.tn.signe}
            {pos.tn.maison > 0 && <span style={{ color: colors.textMuted, marginLeft: 6 }}>M.{pos.tn.maison}</span>}
          </>
        ) : (
          <span style={{ color: colors.textMuted }}>—</span>
        )}
      </td>
      <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>
        {pos.maisonNatale ? (
          <span style={{ background: colors.accentLight, padding: '2px 8px', borderRadius: 4 }}>
            M.{pos.maisonNatale}
          </span>
        ) : '—'}
      </td>
      <td style={{ padding: '8px 10px', textAlign: 'center', color: colors.textMid }}>
        {pos.ecart !== null ? `${pos.ecart}°` : '—'}
      </td>
      {isProgressions && (
        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {pos.changementSigne && (
              <span style={{
                background: '#ede9fe', color: '#7c3aed', padding: '1px 6px',
                borderRadius: 4, fontSize: 10, fontWeight: 600,
              }}>
                Signe
              </span>
            )}
            {pos.changementMaison && (
              <span style={{
                background: '#dbeafe', color: '#2563eb', padding: '1px 6px',
                borderRadius: 4, fontSize: 10, fontWeight: 600,
              }}>
                Maison
              </span>
            )}
          </span>
        </td>
      )}
    </tr>
  );
}

/** Carte d'un axe (AS ou MC) */
function AxeCard({ title, axe, suffix, tnSigne }: {
  title: string;
  axe: { signe: string; degre: string; maisonNatale: number | null; maitre: string | null; maitrePositionRS: { signe: string; maison: number } | null };
  suffix: string;
  tnSigne?: string;
}) {
  return (
    <div style={{
      border: `2px solid ${colors.accent}`, borderRadius: 8, padding: '1rem',
      background: `${colors.accent}08`,
    }}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', color: colors.accent, fontWeight: 700 }}>
        {title}
      </h3>
      <div style={{ display: 'grid', gap: '0.5rem', fontSize: 13 }}>
        <div>
          <span style={{ color: colors.textMid }}>Signe : </span>
          <strong>{signeSymbol(axe.signe)} {axe.signe} {axe.degre}</strong>
        </div>
        {/* Évolution en mode progressions */}
        {tnSigne && tnSigne !== axe.signe && (
          <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600 }}>
            Natal : {signeSymbol(tnSigne)} {tnSigne} → {signeSymbol(axe.signe)} {axe.signe}
          </div>
        )}
        <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>
          → Tombe en <span style={{ background: colors.accentLight, padding: '2px 8px', borderRadius: 4 }}>
            Maison {axe.maisonNatale ?? '?'} natale
          </span>
        </div>
        {axe.maitre && (
          <div style={{ fontSize: 12, color: colors.textMid, marginTop: 4 }}>
            Maître : {planeteSymbol(axe.maitre)} {axe.maitre}
            {axe.maitrePositionRS && (
              <> en {axe.maitrePositionRS.signe} M.{axe.maitrePositionRS.maison} ({suffix})</>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Ligne d'aspect inter-thème */
function AspectRow({ aspect, suffix }: { aspect: AspectInterTheme; suffix: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 10px', borderRadius: 4,
      background: aspect.angulaire ? '#fef2f2' : aspect.echoNatal ? '#eff6ff' : colors.bgPage,
      fontSize: 13,
    }}>
      {/* Planète RS/Prog */}
      <span style={{ width: 90, fontWeight: 600, flexShrink: 0 }}>
        {planeteSymbol(aspect.planeteRS)} {aspect.planeteRS}
        <span style={{ fontSize: 10, color: colors.textMuted, marginLeft: 2 }}>{suffix}</span>
      </span>

      {/* Aspect */}
      <span style={{
        ...natureBadgeStyle(aspect.nature),
        padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500,
        display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
      }}>
        {aspect.symbole} {aspect.type}
      </span>

      {/* Planète TN */}
      <span style={{ width: 90, fontWeight: 600, flexShrink: 0 }}>
        {planeteSymbol(aspect.planeteTN)} {aspect.planeteTN}
        <span style={{ fontSize: 10, color: colors.textMuted, marginLeft: 2 }}>TN</span>
      </span>

      {/* Orbe */}
      <span style={{ color: colors.textMid, fontSize: 12, flexShrink: 0 }}>
        {aspect.orbe}°
      </span>

      {/* Badges */}
      <span style={{ flex: 1, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        {aspect.angulaire && (
          <span style={{
            background: '#fef2f2', color: '#991b1b', padding: '1px 6px',
            borderRadius: 4, fontSize: 10, fontWeight: 600,
          }}>
            Angulaire
          </span>
        )}
        {aspect.echoNatal && (
          <span style={{
            background: '#eff6ff', color: '#1e40af', padding: '1px 6px',
            borderRadius: 4, fontSize: 10, fontWeight: 600,
          }}>
            Écho natal
          </span>
        )}
      </span>
    </div>
  );
}

function categorieIcon(cat: string): string {
  switch (cat) {
    case 'angulaire': return '⊕';
    case 'retour': return '↻';
    case 'echo': return '⇌';
    case 'axe': return '⬡';
    case 'lunaison': return '☽';
    case 'changement': return '↗';
    default: return '•';
  }
}

function categorieIconStyle(cat: string): React.CSSProperties {
  switch (cat) {
    case 'angulaire': return { background: '#fef2f2', color: '#ef4444', fontSize: 14 };
    case 'retour': return { background: '#f0fdf4', color: '#22c55e', fontSize: 14 };
    case 'echo': return { background: '#eff6ff', color: '#3b82f6', fontSize: 14 };
    case 'axe': return { background: '#fefce8', color: '#eab308', fontSize: 14 };
    case 'lunaison': return { background: '#ede9fe', color: '#7c3aed', fontSize: 14 };
    case 'changement': return { background: '#e0f2fe', color: '#0284c7', fontSize: 14 };
    default: return { background: '#f5f5f5', color: '#666', fontSize: 14 };
  }
}
